/**
 * SEO Configuration File
 *
 * This file contains default SEO settings and configurations for the application.
 * These defaults will be used when specific pages don't provide their own meta tags.
 *
 * SEO Best Practices Applied:
 * - Title length: 50-60 characters for optimal SERP display
 * - Description length: 150-160 characters for optimal SERP display
 * - Keywords: Relevant, targeted keywords for insurance industry
 * - Canonical URLs: Prevent duplicate content issues
 * - Open Graph: Enhanced social media sharing
 * - Twitter Cards: Better Twitter presentation
 */

export const seoConfig = {
  // Default site-wide SEO settings
  defaultTitle:
    "Kaphy Insurance Agency | Auto, Home & Commercial Insurance Minneapolis",
  defaultDescription:
    "Trusted Minneapolis insurance agency offering auto insurance, home insurance, renters insurance & commercial insurance. Get free quotes with competitive rates from an independent agency.",
  defaultKeywords:
    "auto insurance, car insurance, home insurance, renters insurance, commercial insurance, business insurance, commercial truck insurance, trucking insurance Minnesota, owner operator insurance Minnesota, Minneapolis insurance, Minnesota insurance, insurance quotes, affordable insurance, independent insurance agency, Kaphy Insurance Agency, Karmel Mall Insurance, Karmel Mall Insurance Agency, Insurance Agency in Karmel Mall, Auto Insurance Karmel Mall, Renters Insurance Karmel Mall, Insurance Near Karmel Mall, Car Insurance Karmel Mall, Insurance Agency Near Lake Street Minneapolis, Insurance Near Cedar Riverside, Independent Insurance Agent Minneapolis",

  // Site information
  siteUrl: "https://www.kaphyinsurance.com",
  siteName: "Kaphy Insurance Agency",
  locale: "en_US",

  // Default Open Graph settings
  defaultOgImage: "https://www.kaphyinsurance.com/social-share.png",
  defaultOgImageWidth: "1200",
  defaultOgImageHeight: "630",
  defaultOgType: "website",

  // Twitter settings
  twitterHandle: "@kaphyinsurance", // Update with your actual Twitter handle
  twitterCardType: "summary_large_image",

  // Business contact information for structured data
  businessInfo: {
    name: "Kaphy Insurance Agency",
    legalName: "Kaphy Insurance Agency",
    url: "https://www.kaphyinsurance.com",
    logo: "https://www.kaphyinsurance.com/kaphy-favicon-16x16.png",
    telephone: "+1-651-444-0402",
    email: "sales@kaphyinsurance.com",
    address: {
      streetAddress: "2910 Pleasant Ave, 1st Floor, Suite 31",
      addressLocality: "Minneapolis",
      addressRegion: "MN",
      postalCode: "55408",
      addressCountry: "US",
    },
    geo: {
      latitude: "44.9497",
      longitude: "-93.2849",
    },
    openingHours: ["Mo-Fr 09:00-19:00", "Sa-Su 13:00-19:00"],
    priceRange: "$$",
    areaServed: [
      "Minneapolis",
      "St. Paul",
      "Brooklyn Park",
      "Maple Grove",
      "Plymouth",
      "Bloomington",
      "Burnsville",
      "Rochester",
      "Mankato",
      "Saint Cloud",
      "Sartell",
      "Sauk Rapids",
      "Waite Park",
      "St. Joseph",
      "Minnesota",
    ],
  },

  // Default robots directive
  defaultRobots:
    "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",

  // Language alternatives (if you have multi-language support)
  languages: {
    en: "https://www.kaphyinsurance.com",
    // Add more languages as needed
    // 'es': 'https://www.kaphyinsurance.com/es',
  },
};

/**
 * Get full URL for a given path
 * @param {string} path - The path to convert to full URL
 * @returns {string} Full URL
 */
export const getFullUrl = (path) => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${seoConfig.siteUrl}${cleanPath}`;
};

/**
 * Sanitize and validate SEO text fields
 * @param {string} text - Text to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized text
 */
export const sanitizeSeoText = (text, maxLength) => {
  if (!text) return "";

  // Remove HTML tags
  const cleanText = text.replace(/<[^>]*>/g, "");

  // Trim to max length
  if (cleanText.length > maxLength) {
    return cleanText.substring(0, maxLength - 3) + "...";
  }

  return cleanText;
};

export default seoConfig;
